import React, { type PropsWithChildren, useCallback, useRef } from 'react';
import { ScrollView, StyleSheet, type View } from 'react-native';
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useBrowserEventListener, useWebKeyListener } from '../../../../../../hooks';
import { FocusScope } from '../../../../../FocusScope';
import { NavbarGlassView } from '../NavbarGlassView/NavbarGlassView';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingPopup = (props: Props) => {
  const { accessibilityLabel, children, onClose, shadow } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const popupRef = useRef<View>(null);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleClickOutside = useCallback(
    (event: Event) => {
      const eventTarget = event?.target;
      const popupElement = popupRef.current as unknown as HTMLElement;

      if (!eventTarget || !popupElement || popupElement.contains(eventTarget as Node)) {
        return;
      }

      onClose();
    },
    [onClose],
  );

  // ─── Effects ───────────────────────────────────────────────────────────────

  useBrowserEventListener(BROWSER_WINDOW, 'click', handleClickOutside);

  useWebKeyListener('Escape', onClose);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <FocusScope>
      <Animated.View
        accessibilityLabel={accessibilityLabel}
        accessibilityViewIsModal={true}
        accessible={true}
        aria-label={accessibilityLabel}
        aria-modal={true}
        role="dialog"
        tabIndex={-1}
        ref={popupRef}
        entering={POPUP_ENTERING_ANIMATION}
        exiting={POPUP_EXITING_ANIMATION}
        style={styles.container}
      >
        <NavbarGlassView rounded="large" shadow={shadow} style={styles.surface}>
          <ScrollView contentContainerStyle={styles.scrollContent} style={styles.scrollView}>
            {children}
          </ScrollView>
        </NavbarGlassView>
      </Animated.View>
    </FocusScope>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const POPUP_ENTERING_ANIMATION = FadeInUp.duration(theme.transitions.short);
const POPUP_EXITING_ANIMATION = FadeOutUp.duration(theme.transitions.short);
const BROWSER_WINDOW = typeof window === 'undefined' ? null : window;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: theme.spacing['20'],
    right: 0,
    bottom: theme.spacing['4'],
    left: theme.spacing['4'],
    zIndex: theme.zIndex.popover,
    maxWidth: 400,
  },
  surface: {
    width: '100%',
    minHeight: 0,
    maxHeight: '100%',
  },
  scrollView: {
    flexShrink: 1,
    minHeight: 0,
  },
  scrollContent: {
    gap: theme.spacing['6'],
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  accessibilityLabel: string;
  onClose: () => void;
  shadow?: boolean;
}>;
